"use client"

import * as React from 'react'
import { Badge } from '@open-mercato/ui/primitives/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@open-mercato/ui/primitives/card'
import { Notice } from '@open-mercato/ui/primitives/Notice'
import { TabEmptyState } from '@open-mercato/ui/backend/detail'
import type { FleetTranslate, FleetVehicleDetail } from './fleetTable'
import {
  formatCapabilityLabel,
  formatEuroClassLabel,
  formatFuelTypeLabel,
  formatGpsProviderLabel,
  formatVehicleTypeLabel,
} from './fleetTable'

function formatNumber(value: number | null, suffix = ''): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—'
  const rendered = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: value % 1 === 0 ? 0 : 3,
  }).format(value)
  return suffix ? `${rendered} ${suffix}`.trim() : rendered
}

function formatDate(value: string | null): string {
  return value ?? '—'
}

function statusVariant(status: FleetVehicleDetail['insuranceStatus']): 'default' | 'destructive' | 'muted' {
  if (status === 'expired') return 'destructive'
  if (status === 'missing') return 'muted'
  return 'default'
}

function statusLabel(t: FleetTranslate, status: FleetVehicleDetail['insuranceStatus']): string {
  if (status === 'expired') return t('fleet.detail.status.expired', 'Expired')
  if (status === 'missing') return t('fleet.detail.status.missing', 'Missing')
  return t('fleet.detail.status.valid', 'Valid')
}

function SummaryField({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) {
  return (
    <div className="space-y-1 rounded-md border bg-card px-3 py-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  )
}

export function FleetOverviewTab({
  vehicle,
  t,
}: {
  vehicle: FleetVehicleDetail
  t: FleetTranslate
}) {
  return (
    <div id="fleet-section-overview" className="grid gap-4 lg:grid-cols-[2fr_1fr]">
      <Card>
        <CardHeader>
          <CardTitle>{t('fleet.detail.overview.summaryTitle', 'Vehicle overview')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <SummaryField label={t('fleet.detail.fields.registrationPlate', 'Registration plate')} value={vehicle.registrationPlate} />
          <SummaryField label={t('fleet.detail.fields.type', 'Type')} value={formatVehicleTypeLabel(t, vehicle.type)} />
          <SummaryField label={t('fleet.detail.fields.brandModel', 'Brand / model')} value={[vehicle.brand, vehicle.model].filter(Boolean).join(' / ') || '—'} />
          <SummaryField label={t('fleet.detail.fields.year', 'Year')} value={vehicle.year ?? '—'} />
          <SummaryField label={t('fleet.detail.fields.payload', 'Payload')} value={formatNumber(vehicle.maxPayloadKg, 'kg')} />
          <SummaryField label={t('fleet.detail.fields.tareWeight', 'Tare weight')} value={formatNumber(vehicle.tareWeightKg, 'kg')} />
          <SummaryField label={t('fleet.detail.fields.euroClass', 'Euro class')} value={formatEuroClassLabel(t, vehicle.euroClass)} />
          <SummaryField label={t('fleet.detail.fields.fuelType', 'Fuel type')} value={formatFuelTypeLabel(t, vehicle.fuelType)} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('fleet.detail.overview.capabilitiesTitle', 'Capabilities')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-1">
            {vehicle.capabilities.length > 0 ? vehicle.capabilities.map((capability) => (
              <Badge key={capability} variant="outline" className="bg-muted/30">
                {formatCapabilityLabel(t, capability)}
              </Badge>
            )) : (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </div>
          <Notice
            compact={true}
            variant="info"
            message={t(
              'fleet.detail.overview.assignmentHookNotice',
              'Dispatch assignment context will be attached here once the road dispatch module is enabled.',
            )}
          />
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>{t('fleet.detail.overview.gpsTitle', 'GPS and telematics')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-3">
          <SummaryField label={t('fleet.detail.fields.gpsProvider', 'GPS provider')} value={formatGpsProviderLabel(t, vehicle.gpsProvider)} />
          <SummaryField label={t('fleet.detail.fields.gpsDeviceId', 'GPS device ID')} value={vehicle.gpsDeviceId ?? '—'} />
          <SummaryField label={t('fleet.detail.fields.recordState', 'Record state')} value={vehicle.isActive ? t('fleet.detail.status.active', 'Active') : t('fleet.detail.status.inactive', 'Inactive')} />
        </CardContent>
      </Card>
    </div>
  )
}

export function FleetDocumentsTab({
  vehicle,
  t,
}: {
  vehicle: FleetVehicleDetail
  t: FleetTranslate
}) {
  return (
    <div id="fleet-section-documents" className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>{t('fleet.detail.documents.insuranceTitle', 'Insurance')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <SummaryField label={t('fleet.detail.documents.expiryDate', 'Expiry date')} value={formatDate(vehicle.insuranceExpires)} />
          <SummaryField label={t('fleet.detail.documents.daysUntil', 'Days until expiry')} value={vehicle.insuranceDaysUntil ?? '—'} />
          <Badge variant={statusVariant(vehicle.insuranceStatus)}>
            {statusLabel(t, vehicle.insuranceStatus)}
          </Badge>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('fleet.detail.documents.inspectionTitle', 'Inspection')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <SummaryField label={t('fleet.detail.documents.expiryDate', 'Expiry date')} value={formatDate(vehicle.inspectionExpires)} />
          <SummaryField label={t('fleet.detail.documents.daysUntil', 'Days until expiry')} value={vehicle.inspectionDaysUntil ?? '—'} />
          <Badge variant={statusVariant(vehicle.inspectionStatus)}>
            {statusLabel(t, vehicle.inspectionStatus)}
          </Badge>
        </CardContent>
      </Card>

      <div className="lg:col-span-2">
        <Notice
          variant="info"
          title={t('fleet.detail.documents.placeholderTitle', 'Document hooks are ready')}
          message={t(
            'fleet.detail.documents.placeholderMessage',
            'Phase 1 exposes expiry monitoring now and leaves room for scanned documents, ADR approvals, and tachograph records later.',
          )}
        />
      </div>
    </div>
  )
}

export function FleetMaintenanceTab({ t }: { t: FleetTranslate }) {
  return (
    <div id="fleet-section-maintenance">
      <TabEmptyState
        title={t('fleet.detail.maintenance.title', 'Maintenance schedule')}
        description={t(
          'fleet.detail.maintenance.description',
          'Phase 1 keeps the slot ready for future service planning without introducing a maintenance subsystem yet.',
        )}
      />
    </div>
  )
}

export function FleetAssignmentHistoryTab({ t }: { t: FleetTranslate }) {
  return (
    <div id="fleet-section-assignment-history">
      <TabEmptyState
        title={t('fleet.detail.assignmentHistory.title', 'Assignment history')}
        description={t(
          'fleet.detail.assignmentHistory.description',
          'Dispatch history hooks are in place; past assignments will appear here once the road dispatch module starts writing them.',
        )}
      />
    </div>
  )
}
